import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Radio, RadioGroup } from '@/components/atoms/RadioGroup';
import PageLayout from '@/components/Layout/PageLayout';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetSwimSpeed } from '@/domain/behavior';
import { isValidCageId } from '@/domain/cages';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import { SWIM_SPEED_THRESHOLD_DEFAULT } from '../BehaviorDashboard/BehaviorDashboardContainer';

import BehaviorSwimSpeedView from './BehaviorSwimSpeedView';
import { useSwimSpeedChartData } from './useSwimSpeedChartData';

export default function BehaviorSwimSpeedContainer() {
    const { t } = useTranslation('behaviorModule');
    const { cageId } = useCage();
    const cageName = useCageName();
    const { selectedFishGroupId } = useCageFilter();

    const { dateFrom, dateTo, selectedPresetId, onDateChange } = useDateRangePicker({
        key: 'swimSpeed',
        defaultPresetId: 'last7days',
    });

    const swimSpeedParams = useMemo(
        () => ({
            from: dateFrom,
            to: dateTo,
            showHourly: selectedPresetId === '24h',
            fishGroupId: selectedFishGroupId,
        }),
        [dateFrom, dateTo, selectedPresetId, selectedFishGroupId]
    );

    const query = useGetSwimSpeed(cageId, swimSpeedParams, {
        enabled: isValidCageId(cageId),
    });
    const { data, isLoading } = query;

    const isFetchingData = !isValidCageId(cageId) || isLoading;

    const swimSpeedDataPoints = data?.data ?? [];
    const swimSpeedThreshold = SWIM_SPEED_THRESHOLD_DEFAULT;
    const lastMeasurement = data?.lastMeasurement ?? '';

    const { chart, hasOxygenData, selectedRightAxisValue, setSelectedRightAxisValue } =
        useSwimSpeedChartData({
            swimSpeedDataPoints,
            swimSpeedThreshold,
        });

    const legendRight = (
        <RadioGroup
            direction="horizontal"
            value={selectedRightAxisValue}
            onValueChange={setSelectedRightAxisValue}>
            <Radio value="temperature" label={t('common.temperature')} />
            <Radio value="depth" label={t('common.depth')} />
            {hasOxygenData && <Radio value="oxygen" label={t('common.oxygen')} />}
        </RadioGroup>
    );

    return (
        <PageLayout title={cageName} subtitle={t('swimSpeedView.title')}>
            <BehaviorSwimSpeedView
                chart={chart}
                swimSpeedThreshold={swimSpeedThreshold}
                lastMeasurement={lastMeasurement}
                isLoading={isFetchingData}
                error={query.error}
                retry={retryFailedQueries(query)}
                legendRight={legendRight}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}
